import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Building, DollarSign, ArrowLeft, Briefcase, Globe, Award, Users, Share2, Check } from 'lucide-react';

const PublicJobDetail = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPosition();
  }, [id]);

  const fetchPosition = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/positions/${id}`
      );
      if (response.data.success) {
        setPosition(response.data.data);
        setOrg(response.data.data.organizations);
      }
    } catch (error) {
      console.error('Error fetching position:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Position not found</h1>
          <p className="text-gray-600">This position may have been closed or removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-job-detail">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            {org?.logo_url ? (
              <img src={org.logo_url} alt={org.name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
            )}
            <Link to={`/${slug}/about`} className="text-lg font-semibold text-gray-900 hover:text-blue-600">
              {org?.name || 'Company'}
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to jobs */}
        <Link
          to={`/${slug}/jobs`}
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mb-6"
          data-testid="back-to-jobs"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all jobs
        </Link>

        {/* Title + Apply */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="job-title">
              {position.title}
            </h1>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
              {position.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {position.location}
                </span>
              )}
              {position.employment_type && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> <span className="capitalize">{position.employment_type}</span>
                </span>
              )}
              {position.workplace && (
                <span className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4" /> <span className="capitalize">{position.workplace}</span>
                </span>
              )}
              {(position.salary_min || position.salary_max) && (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" />
                  {position.salary_currency || 'EUR'} {position.salary_min?.toLocaleString()}
                  {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                </span>
              )}
              {position.experience_level && (
                <span className="flex items-center gap-1.5">
                  <Award className="w-4 h-4" /> <span className="capitalize">{position.experience_level}</span>
                </span>
              )}
              {position.number_of_positions > 1 && (
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> {position.number_of_positions} openings
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 ml-4">
            <button
              onClick={handleShare}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium inline-flex items-center gap-2"
              data-testid="share-job-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Share'}
            </button>
            <button
              onClick={() => navigate(`/${slug}/jobs/${id}/apply`)}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
              data-testid="apply-btn"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Skills */}
        {position.must_have_skills && position.must_have_skills.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {position.must_have_skills.map((skill, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                  {skill}
                </span>
              ))}
              {position.nice_to_have_skills?.map((skill, idx) => (
                <span key={`nice-${idx}`} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Job Description Sections */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 space-y-8" data-testid="job-content">
          {position.description && position.description !== '<p><br></p>' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">About the Role</h2>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.description }} />
            </div>
          )}

          {position.responsibilities && position.responsibilities !== '<p><br></p>' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Responsibilities</h2>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.responsibilities }} />
            </div>
          )}

          {position.profile_requirements && position.profile_requirements !== '<p><br></p>' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Profile & Requirements</h2>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.profile_requirements }} />
            </div>
          )}

          {position.benefits && position.benefits !== '<p><br></p>' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Benefits</h2>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.benefits }} />
            </div>
          )}

          {position.languages && position.languages.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Required Languages</h2>
              <div className="flex flex-wrap gap-2">
                {position.languages.map((lang, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Apply CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(`/${slug}/jobs/${id}/apply`)}
            className="px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-base font-semibold"
            data-testid="apply-bottom-btn"
          >
            Apply for this position
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-sm text-gray-500">
          Powered by <span className="font-semibold text-gray-700 notranslate" translate="no">JobastiIQ</span>
        </div>
      </div>
    </div>
  );
};

export default PublicJobDetail;
